import * as angular from "angular";
import {IController} from "angular";
import {
  JobSuggestion,
  JobSuggestionDecision,
  JobSuggestionStatus,
  JobSuggestionsType
} from "../../models/job-suggestion.model";
import {PackageModel} from "../../../commons/models/package.model";
import * as _ from "lodash";

interface SubJobTotals {
  count: number;
  weight: number;
  pallets: number;
  volume: number;
}

enum TotalsUpdateOperation {
  Add = 1,
  Remove = -1
}

const baseTotalPreComputations: SubJobTotals = {
  count: 0,
  weight: 0,
  pallets: 0,
  volume: 0
}

type LocationType = "DEPOT" | "ACCOUNT";
type StopType = "Pickup" | "Dropoff";

export class PlanningDraftBulkJobSplitFormComponent implements IController {

  suggestions: any[];

  public SplitSuggestionType = JobSuggestionsType;
  public SuggestionDecision = JobSuggestionDecision;

  public selectedSplitType: JobSuggestionsType;
  public options = [
    {value: JobSuggestionsType.SPLIT_PACKAGES, icon: 'BOX'},
    {value: JobSuggestionsType.SPLIT_LOCATION, icon: 'location-pin-outline'}
  ];
  public accountIds: string[] = [];
  private _accountIdsSet = new Set<string>();
  public serviceAreaIds: string[] = [];
  private _serviceAreaIdsSet = new Set<string>();
  public tags: string[] = [];
  private _query: string = '';
  public derivedSuggestions: any[];
  private _sitePackageItemsMap;
  public isAnySuggestion: boolean;
  private _suggestionToTypeToPackageMap = new Map<string, { [key: string]: PackageModel[] }>();
  public selectedSuggestionsForBulkActions: any[] = [];
  public areAllVisibleSelectableChecked: boolean = false;
  private _selectableSuggestionsIds = new Set<string>();
  private _approvedJobIdToSuggestionIdMap = new Map<string, string>();
  public subJobToPackagesMap = {};
  public subJobTotalsMap = {};

  private _splitPackageTotals: Map<string, { locationType: LocationType, contactName: string, totals: SubJobTotals, nCount: number }> = new Map();
  private _splitLocationTotals: Map<string, { from: { contactName: string, locationType: LocationType, city?: string }, to: { contactName: string, locationType: LocationType, city?: string }, totals: SubJobTotals, nCount: number }> = new Map();
  public splitLocationTotalsValues: {from: { contactName: string, locationType: LocationType }, to: { contactName: string, locationType: LocationType, city?: string }, totals: SubJobTotals, nCount: number}[] = [];
  public splitPackagesTotalsValues: {locationType: LocationType, contactName: string, totals: SubJobTotals, nCount: number}[] = [];

  public overallSelectedTotals = {...baseTotalPreComputations };


  constructor(private splitService) {
  }

  $onInit() {
    // Compute custom ID (referencedJobId)
    this.suggestions = this.suggestions.map(suggestion => ({
      ...suggestion,
      // only rejected suggestions can re-appear atm
      decision: suggestion.status === JobSuggestionStatus.REJECTED ? JobSuggestionDecision.DENY : null,
      referencedJobId: suggestion.suggestionInformation.originalJobSnapshot.externalId ||
        suggestion.suggestionInformation.originalJobSnapshot.orderId ||
        suggestion.suggestionInformation.originalJobSnapshot.fulfillmentStepId
    }));

    this.suggestions.sort((a, b) => a.referencedJobId.localeCompare(b.referencedJobId));
    this.isAnySuggestion = this.suggestions && this.suggestions.length > 0;
    this._sitePackageItemsMap = this.splitService.getSitePackageItemsMap();

    this._rebuildApprovedSuggestionMap();
    this._computeSubJobPackages();
    this._computeSuggestionOriginalStateTotals();
    this._buildSuggestionPackagesMap();
    this._applyFilters();
  }

  onSplitTypeSelect(splitType: JobSuggestionsType) {
    this.selectedSplitType = this.selectedSplitType !== splitType ? splitType : null;
    this._applyFilters();
  }

  onAccountsChange(selectedIds: string[]) {
    this.accountIds = selectedIds;
    this._accountIdsSet = new Set(this.accountIds);
    this._applyFilters();
  }

  onServiceAreasChange(selectedIds: string[]) {
    this.serviceAreaIds = selectedIds;
    this._serviceAreaIdsSet = new Set(this.serviceAreaIds);
    this._applyFilters();
  }

  onTagChange(tags: string[]) {
    this.tags = tags;
    this._applyFilters();
  }

  getJobStopAddressByType(job, stopType: StopType) {
    return job.stops.find(stop => stop.stopType === stopType).address;
  }

  getNumberOfApprovedSuggestions() {
    return this._approvedJobIdToSuggestionIdMap.size
  }

  setDecision(suggestion, decision: JobSuggestionDecision) {
    if (this.isSuggestionDisabled(suggestion)) {
      return;
    }

    let op: TotalsUpdateOperation;
    if(suggestion.decision !== JobSuggestionDecision.APPROVED && decision === JobSuggestionDecision.APPROVED) {
      // Set decision TO `Approved` → increase the totals
      op = TotalsUpdateOperation.Add;
    } else if(suggestion.decision === JobSuggestionDecision.APPROVED) {
      // Set decision FROM `Approved` → decrease totals
      op = TotalsUpdateOperation.Remove;
    }

    suggestion.decision = suggestion.decision === decision ? null : decision;

    const index = this.suggestions.findIndex(s => s.id === suggestion.id);
    this.suggestions[index] = suggestion;

    // Uncheck other suggestions if there are multiple when APPROVED is chosen
    if (suggestion.decision === JobSuggestionDecision.APPROVED) {
      this.selectedSuggestionsForBulkActions = this.selectedSuggestionsForBulkActions.filter(s =>
        s.referencedJobId !== suggestion.referencedJobId ||
        s.id === suggestion.id
      );
    }

    this._addOrRemoveTotalsForApprovedSuggestions(suggestion, op);
    this._updateApprovedSuggestionMap(suggestion);
    this._applyFilters();
  }

  setBulkDecisionOnSuggestions(decision: JobSuggestionDecision) {
    // Use copy since `setDecision` can modify the selection array
    const selectableSuggestionsCopy = [...this.selectedSuggestionsForBulkActions];
    switch (decision) {
      case JobSuggestionDecision.DENY:
        selectableSuggestionsCopy.forEach(s => {
          if (this._selectableSuggestionsIds.has(s.id)) {
            this.setDecision(s, JobSuggestionDecision.DENY)
          }
        });
        break;
      case JobSuggestionDecision.APPROVED:
        // Note that in case of multiple suggestions for the same job, only one is approved
        // Which one is approved depends on the order of selection
        selectableSuggestionsCopy.forEach(s => {
          if (this._selectableSuggestionsIds.has(s.id) && !this._approvedJobIdToSuggestionIdMap.has(s.referencedJobId)) {
            this.setDecision(s, JobSuggestionDecision.APPROVED);
          }
        });
        break;
      case JobSuggestionDecision.OPTIMIZE:
        break;
      default:
        break;
    }

    this._applyFilters();
  }

  onQueryChanged(query) {
    this._query = query;
    this._applyFilters();
  }

  getPackageActualName(pkg) {
    if (pkg.size) {
      return this._sitePackageItemsMap[pkg.size].name;
    } else {
      return this._sitePackageItemsMap[pkg.type].name;
    }
  }

  onSuggestionEdit(suggestion) {
    if (this.isSuggestionDisabled(suggestion)) {
      return;
    }
    // TODO - future implementation, behaviour not yet defined as of CON-12500
  }

  isSuggestionDisabled(suggestion) {
    // Approved suggestion should be active; all other suggestions for the same job should be disabled
    if (suggestion.decision === JobSuggestionDecision.APPROVED) {
      return false;
    }

    const approvedSuggestionId = this._approvedJobIdToSuggestionIdMap.get(suggestion.referencedJobId);
    return !!approvedSuggestionId && approvedSuggestionId !== suggestion.id;
  }

  isSuggestionSelected(suggestion) {
    return this.selectedSuggestionsForBulkActions.some(s => s.id === suggestion.id);
  }

  getPkgMapForSuggestion(suggestion: JobSuggestion): { [key: string]: PackageModel[] } {
    return this._suggestionToTypeToPackageMap.get(suggestion.id) || {};
  }

  toggleSelectionOfJobSuggestion(suggestion) {
    if (!this._selectableSuggestionsIds.has(suggestion.id)) {
      return;
    }

    const idx = this.selectedSuggestionsForBulkActions.findIndex(s => s.id === suggestion.id);
    if (idx === -1) {
      this.selectedSuggestionsForBulkActions = [suggestion, ...this.selectedSuggestionsForBulkActions];
    } else {
      this.selectedSuggestionsForBulkActions = this.selectedSuggestionsForBulkActions.filter(s => s.id !== suggestion.id);
    }

    this._updateSelectionOfAllSuggestionsStatus();
  }

  onToggleSelectAll() {
    // Computing selected suggestions for bulk actions from the current state of view (derived suggestions - from filters)
    const visibleSelectable = this.derivedSuggestions
      .filter(s => this._selectableSuggestionsIds.has(s.id));

    if (!this.areAllVisibleSelectableChecked) {
      const selectedById = new Map<string, any>(this.selectedSuggestionsForBulkActions.map(s => [s.id, s]));
      visibleSelectable.forEach(s => selectedById.set(s.id, s));
      this.selectedSuggestionsForBulkActions = Array.from(selectedById.values());
    } else {
      const visibleIds = new Set<string>(visibleSelectable.map(s => s.id));
      this.selectedSuggestionsForBulkActions = this.selectedSuggestionsForBulkActions.filter(s => !visibleIds.has(s.id));
    }

    this._updateSelectionOfAllSuggestionsStatus();
  }

  private _convertSelectedDestinationTotalsToValues() {
    this.splitLocationTotalsValues = Array.from(this._splitLocationTotals.values());
    this.splitPackagesTotalsValues = Array.from(this._splitPackageTotals.values());
  }

  private _rebuildApprovedSuggestionMap() {
    // Rebuilds approval map from scratch - use from OnInit, bulk approval to keep track when view changes from filters
    this._approvedJobIdToSuggestionIdMap.clear();
    this.suggestions.forEach((s) => {
      if (s.decision === JobSuggestionDecision.APPROVED) {
        this._approvedJobIdToSuggestionIdMap.set(s.referencedJobId, s.id);
      }
    });
  }

  private _updateApprovedSuggestionMap(suggestion) {
    // Updates approval map - add or remove based on the state of suggestion decision
    const referencedJobId = suggestion.referencedJobId;

    if (suggestion.decision === JobSuggestionDecision.APPROVED) {
      this._approvedJobIdToSuggestionIdMap.set(referencedJobId, suggestion.id);
      return;
    }

    const currentApprovedId = this._approvedJobIdToSuggestionIdMap.get(referencedJobId);
    if (currentApprovedId === suggestion.id) {
      this._approvedJobIdToSuggestionIdMap.delete(referencedJobId);
    }
  }

  private _mapTypeToPackages(packages: PackageModel[]) {
    const typeToPackagesMap: { [key: string]: PackageModel[] } = {};
    packages.forEach(pkg => {
      const pkgName = this.getPackageActualName(pkg);
      if (!typeToPackagesMap[pkgName]) {
        typeToPackagesMap[pkgName] = [];
      }
      typeToPackagesMap[pkgName].push(pkg);
    });
    return typeToPackagesMap;
  }

  private _updateSelectionOfAllSuggestionsStatus() {
    // Computes the status of the flag `areAllVisibleSelectableChecked` checkbox
    const selectedIds = new Set<string>(this.selectedSuggestionsForBulkActions.map(s => s.id));

    const visibleSelectable = this.derivedSuggestions
      .filter(s => this._selectableSuggestionsIds.has(s.id));

    if (visibleSelectable.length === 0) {
      this.areAllVisibleSelectableChecked = false;
      return;
    }

    this.areAllVisibleSelectableChecked = visibleSelectable.every(s => selectedIds.has(s.id));
  }

  private _buildSuggestionPackagesMap() {
    // Build two layered map - For each suggestion, a map of type which points to all the packages of its type
    if (!this.isAnySuggestion) {
      return;
    }
    this._suggestionToTypeToPackageMap.clear();

    this.suggestions.forEach((suggestion) => {
      const packages = suggestion.suggestionInformation.originalJobSnapshot.packages || [];
      this._suggestionToTypeToPackageMap.set(suggestion.id, this._mapTypeToPackages(packages));
    });
  }

  private _computeSubJobTotals(subJobId: string, packages: any[]) {
    let subJobTotals = {...baseTotalPreComputations};
    packages.forEach(pkg => {
      subJobTotals.count += (pkg.count || 0);
      subJobTotals.weight += (pkg.weight || 0);
      subJobTotals.volume += (pkg.volume || 0);
      subJobTotals.pallets += (pkg.pallets || 0);
    });
    this.subJobTotalsMap[subJobId] = subJobTotals;
  }

  private _computeSubJobPackages() {
    // SubJobId to Packages map - used in cell for `Recommended Change` in case of Split by packages
    // Calls to _subJobToTotalsMap to update totals for each subJob on its packages
    this.suggestions.forEach(suggestion =>
      suggestion.suggestionInformation.subJobs.forEach(subJob => {
          this.subJobToPackagesMap[subJob.id] = subJob.packages;
          this._computeSubJobTotals(subJob.id, subJob.packages);
        }
      )
    );
  }

  private _computeSuggestionOriginalStateTotals() {
    this.suggestions.forEach(suggestion => {
      const packages = suggestion.suggestionInformation.originalJobSnapshot.packages || [];
      this._computeSubJobTotals(suggestion.suggestionInformation.originalJobSnapshot.id, packages);
    });
  }

  private _computeSelectableSuggestionsIds(suggestions: any[]): Set<string> {
    const selectable = new Set<string>();

    // Group suggestions by job id
    const jobIdToSuggestionsMap = new Map<string, any[]>();
    suggestions.forEach(s => {
      const arr = jobIdToSuggestionsMap.get(s.referencedJobId) ?? [];
      arr.push(s);
      jobIdToSuggestionsMap.set(s.referencedJobId, arr);
    });

    jobIdToSuggestionsMap.forEach(jobSuggestions => {
      const approved = jobSuggestions.filter(s => s.decision === JobSuggestionDecision.APPROVED);

      if (approved.length > 0) {
        // If any approved exists for that job, only APPROVED is selectable
        approved.forEach(s => selectable.add(s.id));
      } else {
        jobSuggestions.forEach(s => selectable.add(s.id));
      }
    });

    return selectable;
  }

  private _addOrRemoveTotalsForApprovedSuggestions(suggestion, op: TotalsUpdateOperation) {
    // Updates overall totals and totals per destination of approved suggestions
    if(!op) {
      return;
    }

    // If split of type `location` we care only about the first subJob
    let subJobs = [];
    if(suggestion.suggestionInformation.type === JobSuggestionsType.SPLIT_LOCATION) {
      subJobs = [suggestion.suggestionInformation.subJobs[0]];
    } else {
      subJobs = [...suggestion.suggestionInformation.subJobs];
    }

    subJobs.forEach((subJob) => {
      const dropOffStop = subJob.stops.find(stop => stop.stopType === 'Dropoff');
      const locationId = dropOffStop?.address?.locationId;
      const locationType: LocationType = dropOffStop?.address?.locationType;
      const contactName = dropOffStop?.contact?.name;

      const subJobTotals = this.subJobTotalsMap[subJob.id];

      this.overallSelectedTotals = {
        count: this.overallSelectedTotals.count + ((subJobTotals.count * op) || 0),
        weight: this.overallSelectedTotals.weight + ((subJobTotals.weight * op) || 0),
        volume: this.overallSelectedTotals.volume + ((subJobTotals.volume * op) || 0),
        pallets: this.overallSelectedTotals.pallets + ((subJobTotals.pallets * op) || 0),
      };

      if (!locationId) {
        return;
      }

      // Update `totals` map according to split type
      if(suggestion.suggestionInformation.type === JobSuggestionsType.SPLIT_LOCATION) {
        const to: { contactName: string, locationType: LocationType, city?: string } = {
          contactName,
          locationType,
          city: dropOffStop?.address?.city
        };

        const pickUpStop = subJob.stops.find(stop => stop.stopType === 'Pickup');
        const fromLocationId = pickUpStop?.address?.locationId;
        const fromLocationType: LocationType = pickUpStop?.address?.locationType;
        const fromContactName = pickUpStop?.contact?.name;
        const from: { contactName: string, locationType: LocationType, city?: string } = {
          contactName: fromContactName,
          locationType: fromLocationType,
          city: pickUpStop?.address?.city
        };

        const key = `${fromLocationId}-${locationId}`;

        const existing = this._splitLocationTotals.get(key);

        if (!existing) {
          // First contribution for that destination (select flow only)
          this._splitLocationTotals.set(key, {
            from,
            to,
            totals: { ...subJobTotals },
            nCount: 1
          });
          return;
        }

        // Count used to track when we can drop the key
        const updatedCount = existing.nCount + op;

        const nextTotals: SubJobTotals = {
          count: existing.totals.count + ((subJobTotals.count || 0) * op),
          weight: existing.totals.weight + ((subJobTotals.weight || 0) * op),
          volume: existing.totals.volume + ((subJobTotals.volume || 0) * op),
          pallets: existing.totals.pallets + ((subJobTotals.pallets || 0) * op),
        };

        if (updatedCount > 0) {
          this._splitLocationTotals.set(key, {
            from,
            to,
            totals: nextTotals,
            nCount: updatedCount
          });
        } else {
          this._splitLocationTotals.delete(key);
        }
      } else {
        const existing = this._splitPackageTotals.get(locationId);

        if (!existing) {
          // First contribution for that destination (select flow only)
          this._splitPackageTotals.set(locationId, {
            locationType,
            contactName,
            totals: { ...subJobTotals },
            nCount: 1
          });
          return;
        }

        // Count used to track when we can drop the key
        const updatedCount = existing.nCount + op;

        const nextTotals: SubJobTotals = {
          count: existing.totals.count + ((subJobTotals.count || 0) * op),
          weight: existing.totals.weight + ((subJobTotals.weight || 0) * op),
          volume: existing.totals.volume + ((subJobTotals.volume || 0) * op),
          pallets: existing.totals.pallets + ((subJobTotals.pallets || 0) * op),
        };

        if (updatedCount > 0) {
          this._splitPackageTotals.set(locationId, {
            locationType: existing.locationType ?? locationType,
            contactName: existing.contactName ?? contactName,
            totals: nextTotals,
            nCount: updatedCount
          });
        } else {
          this._splitPackageTotals.delete(locationId);
        }
      }
    });

    this._convertSelectedDestinationTotalsToValues();
  }

  private _filterByQuery(suggestion) {
    return suggestion.referencedJobId.includes(this._query);
  }

  private _filterBySplitType = (suggestion) => {
    return !this.selectedSplitType ||
      suggestion.suggestionInformation.type === this.selectedSplitType;
  };

  private _filterByAccount = (suggestion) => {
    return this._accountIdsSet.size === 0 ||
      this._accountIdsSet.has(suggestion.suggestionInformation.originalJobSnapshot.accountId);
  };

  private _filterByServiceArea = (suggestion) => {
    return this._serviceAreaIdsSet.size === 0 ||
      suggestion.suggestionInformation.originalJobSnapshot.areaIds.some((areaId) => this._serviceAreaIdsSet.has(areaId));
  };

  private _filterByTags = (suggestion) => {
    const hasNoTags = _.find(this.tags, (t) => {
      return t === 'noTag';
    })
    return !this.tags?.length ||
      (!!hasNoTags && !suggestion.suggestionInformation.originalJobSnapshot.tags?.length) ||
      !!_.intersection(suggestion.suggestionInformation.originalJobSnapshot.tags, this.tags).length;
  };

  private _applyFilters() {
    this.derivedSuggestions = this.suggestions
      .filter(suggestion => this._filterByQuery(suggestion))
      .filter(suggestion => this._filterBySplitType(suggestion))
      .filter(suggestion => this._filterByAccount(suggestion))
      .filter(suggestion => this._filterByServiceArea(suggestion))
      .filter(suggestion => this._filterByTags(suggestion));

    this._selectableSuggestionsIds = this._computeSelectableSuggestionsIds(this.derivedSuggestions);

    this._updateSelectionOfAllSuggestionsStatus();
  }
}

angular
  .module('bringoz.admin.planning')
  .component('planningDraftBulkJobSplitForm', {
    bindings: {
      suggestions: '='
    },
    template: require('./planning-draft-bulk-job-split-form.html').default,
    controller: PlanningDraftBulkJobSplitFormComponent
  });
